// §4.5 Cycle counts — the LIST, and the reviewer's inbox.
//
// The §4.5 review screen works on ONE count, so this list is how both roles
// reach one: a reviewer finds what is waiting on them, and a storekeeper finds
// (or starts) the count they are about to walk. Reachable from the nav on any
// of the three count codes (inventory-layout.tsx, surface review F1); the
// route itself renders a not-available note rather than a blank screen for
// anyone who lands on it, because the server is the real gate and a 403 list
// is not an explanation.
//
// THE DEFAULT FILTER IS THE CALLER'S OWN QUESTION (F1/F15, 2026-08-12). It was
// hardcoded to `review` — the reviewer's question — so a storekeeper who
// pressed "Cycle counts" met a filtered-to-empty table under the words
// "Nothing to review", directly below a note telling them they cannot review.
// Three statements, one screen, and the only true one was the note. A caller
// holding `count.review` still lands on the review queue; everyone else lands
// on all statuses and is told, in their own terms, what they are looking at.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable } from "../../../../components/data-table";
import { CheckboxField } from "../../../../components/field";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { CursorPager, FacilityChip } from "../components/filters";
import { InventoryStatus, MoneyCell, MoneyColumnNote } from "../components/inventory-ui";
import { LocationPicker } from "../components/location-picker";
import {
  useCreateCycleCount,
  useCycleCounts,
  type CountsQuery,
  type CycleCount,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { costPresence, showCostColumn } from "../lib/cost";
import { inventoryErrorMessage } from "../lib/errors";
import { formatTimestamp } from "../lib/measure";

const COUNT_STATUSES = [
  "draft",
  "in_progress",
  "review",
  "pending_approval",
  "posted",
  "cancelled",
] as const;

export function CountsListScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();

  // `undefined` = the caller has not chosen; the DEFAULT is then derived from
  // what they may do, so it follows the role feed when it settles instead of
  // freezing whatever was known on the first render (fail-closed = no grants).
  const [pickedStatus, setPickedStatus] = useState<string | undefined>(undefined);
  const [facilityId, setFacilityId] = useState("");
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [createOpen, setCreateOpen] = useState(false);
  const cursor = cursors[cursors.length - 1];
  const canPerform = perms.has("inventory.count.perform");
  const canReview = perms.has("inventory.count.review");
  const status = pickedStatus ?? (canReview ? "review" : "");
  const setStatus = setPickedStatus;

  const query = useMemo(() => {
    const next: CountsQuery = { limit: 50 };
    if (cursor !== undefined) next.cursor = cursor;
    if (status.length > 0) next.status = status;
    if (facilityId.length > 0) next.facility_id = facilityId;
    return next;
  }, [cursor, status, facilityId]);

  const counts = useCycleCounts(query);

  useEffect(() => {
    if (counts.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [counts.error, navigate]);

  const items = counts.data?.items ?? [];
  const presence = costPresence(items, (row) => row.variance_value_minor);
  const withValue = showCostColumn(presence);

  return (
    <section className="counts-list">
      <PageHeader
        title={term("cycle_count")}
        actions={
          <>
            <FacilityChip value={facilityId} onChange={setFacilityId} />
            {/* §5 row 35 — `inventory.count.perform` is granted to storekeeper
              and floor_worker; without this control the office could review
              counts it had no way to start. */}
            {canPerform ? (
              <Button
                variant="primary"
                onClick={() => {
                  setCreateOpen((prev) => !prev);
                }}
              >
                {t("inventory.counts.new")}
              </Button>
            ) : null}
          </>
        }
      />

      {createOpen && canPerform ? (
        <CountCreatePanel
          facilityId={facilityId}
          onClose={() => {
            setCreateOpen(false);
          }}
        />
      ) : null}

      {!perms.isResolved || canReview ? null : (
        <p className="degraded-note" role="note">
          {canPerform
            ? t("inventory.counts.performOnlyGrant")
            : t("inventory.counts.noReviewGrant")}
        </p>
      )}

      <div className="filter-bar">
        <label className="filter-chip">
          <span className="filter-chip-label">{t("inventory.counts.status")}</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setCursors([undefined]);
            }}
          >
            <option value="">{t("inventory.counts.allStatuses")}</option>
            {COUNT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`inventory.vocab.count.${value}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {counts.isPending ? (
        <TableSkeleton rows={5} cols={4} label={t("inventory.counts.loading")} />
      ) : null}

      {counts.error && !(counts.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.counts.errorTitle")}
          detail={counts.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void counts.refetch()}
          requestId={requestIdOf(counts.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {counts.data !== undefined && items.length === 0 ? (
        <EmptyState
          // "Nothing to review" is a REVIEWER's sentence. Said to a
          // storekeeper — who was just told they cannot review — it named a
          // job they do not have (F15).
          title={
            canReview && status === "review"
              ? t("inventory.counts.emptyTitle")
              : t("inventory.counts.emptyListTitle")
          }
          body={
            canPerform && status.length === 0
              ? t("inventory.counts.emptyPerformBody")
              : t("inventory.counts.emptyBody")
          }
          {...(canPerform
            ? {
                ctaLabel: t("inventory.counts.new"),
                onCta: () => {
                  setCreateOpen(true);
                },
              }
            : {})}
        />
      ) : null}

      {items.length > 0 ? (
        <>
          <MoneyColumnNote presence={presence} />
          <DataTable
            columns={[
              {
                key: "count_code",
                header: t("inventory.counts.colCode"),
                id: true,
                render: (count) => (
                  <Link to="/inventory/counts/$id" params={{ id: count.id }} className="ds-lnk">
                    {count.count_code}
                  </Link>
                ),
              },
              {
                key: "status",
                header: t("inventory.counts.colStatus"),
                render: (count) => <InventoryStatus status={count.status} scope="count" />,
              },
              {
                key: "blind",
                header: t("inventory.counts.colBlind"),
                render: (count) =>
                  count.blind ? t("inventory.counts.blind") : t("inventory.counts.open"),
              },
              {
                key: "submitted_at",
                header: t("inventory.counts.colSubmitted"),
                age: true,
                render: (count) =>
                  count.submitted_at === null
                    ? "—"
                    : formatTimestamp(count.submitted_at, i18n.language),
              },
              // §7 redaction-by-absence: no column at all without the key.
              ...(withValue
                ? [
                    {
                      key: "variance_value_minor",
                      header: t("inventory.counts.colVarianceValue"),
                      numeric: true,
                      render: (count: CycleCount) => (
                        <MoneyCell
                          row={count}
                          valueOf={(row) => row.variance_value_minor}
                          currencyOf={(row) => row.currency}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            rows={items}
            rowKey={(count) => count.id}
            empty={null}
          />
          <CursorPager
            canPrev={cursors.length > 1}
            nextCursor={counts.data?.next_cursor}
            onPrev={() => {
              setCursors((prev) => prev.slice(0, -1));
            }}
            onNext={(next) => {
              setCursors((prev) => [...prev, next]);
            }}
          />
        </>
      ) : null}
    </section>
  );
}

/**
 * §5 row 35 — open a count over a COUNTABLE location. The scope select offers
 * only countable, active, physical locations: §2.1 refuses anything else with
 * `LOCATION_NOT_COUNTABLE`, and offering those would be a control that exists
 * to be refused.
 *
 * ABC scheduling (`/count-schedules`, `inventory.count_schedule.manage`) is a
 * separate screen and is NOT built in this slice — recorded as a named
 * follow-up in the §4.5 row rather than left as a silent gap.
 */
function CountCreatePanel({ facilityId, onClose }: { facilityId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const create = useCreateCycleCount();
  const [scope, setScope] = useState("");
  const [blind, setBlind] = useState(true);

  return (
    <section className="inv-panel" aria-label={t("inventory.counts.new")}>
      <div className="inv-panel-head">
        <h2>{t("inventory.counts.new")}</h2>
        <Button className="inv-panel-close" onClick={onClose}>
          {t("inventory.common.close")}
        </Button>
      </div>
      {create.isSuccess ? (
        <>
          <p className="inv-success" role="status">
            {t("inventory.counts.created", { code: create.data.count.count_code })}
          </p>
          <p className="inv-hint">{t("inventory.counts.worksheetOnFloor")}</p>
          <div className="form-actions">
            <Link
              to="/inventory/counts/$id"
              params={{ id: create.data.count.id }}
              className="ds-lnk"
            >
              {t("inventory.counts.openCreated")}
            </Link>
          </div>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate({ scope_location_id: scope, blind });
          }}
        >
          {/* COUNTABLE + ACTIVE are now server filters (§5 row 1), so the choice
              is over every countable location in the facility rather than over
              whichever ones survived a 200-row page and a client-side filter —
              which, on a plant with more bins than that, silently offered a
              storekeeper a subset with no way to reach the rest. The virtual
              kinds are excluded by construction: the module provisions all 8
              with is_countable = false and refuses to toggle them (§2.1). */}
          <LocationPicker
            testId="count-scope"
            label={term("location")}
            value={scope}
            onChange={setScope}
            facilityId={facilityId}
            isCountable
            isActive
            emptyLabel={t("inventory.counts.noCountable")}
          />
          <CheckboxField
            label={t("inventory.counts.blindToggle")}
            checked={blind}
            onChange={setBlind}
          />
          <p className="inv-hint">{t("inventory.counts.blindHint")}</p>
          {create.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, create.error, "inventory.counts.createFailed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={create.isPending}
              disabled={scope.length === 0}
            >
              {t("inventory.counts.createSubmit")}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
